import React, { useMemo, useState } from 'react'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { PrimaryChip } from 'components/Shared/PrimaryChip'
import { useField } from 'formik'
import { isFieldRequired } from 'components/Shared/FormikForm'
import { useValidationSchema } from 'context/validationSchema'
import * as Yup from 'yup'
import { ErrorMessage } from 'components/Shared/ErrorMessage'

const invalidEmailMessage = 'form.order.email'
const requiredMessage = 'form.validation.required'
const emailSchema = Yup.string().email(invalidEmailMessage)

const useStyles = makeStyles({
  root: {
    textAlign: 'left',
    maxWidth: 308
  },
  input: {
    padding: '-10px',
    width: '100% !important'
  },
  disabledChipContainer: {
    cursor: 'auto'
  }
})

function MailChip ({ text, disabled, onDelete, onEdit }) {
  return (
    <PrimaryChip
      key={text}
      onClick={() => onEdit()}
      onDelete={onDelete}
      label={text}
      disabled={disabled}
    />
  )
}

export function AutosuggestionMailInput ({ name, placeholder, disabled, suggestions }) {
  const classes = useStyles()
  const { schema } = useValidationSchema()
  const [, meta, helpers] = useField(name)
  const { setValue, setTouched, setError } = helpers
  const [inputValue, setInputValue] = useState('')

  const { value, touched } = meta

  const isRequired = useMemo(() => isFieldRequired(schema, name), [schema, name])

  const handleAdd = (event, values) => {
    const isValid = values.map(val => verifyValidationSchema(val)).every(valid => valid === true)

    if (isValid) {
      setValue(values)
      setInputValue('')
      setError(null)
    }
  }

  function verifyValidationSchema (email) {
    const isValid = emailSchema.isValidSync(email)
    if (!isValid) {
      setError(invalidEmailMessage)
    }
    return isValid
  }

  function handleEditClick (email) {
    remove(email)
    setInputValue(email)
  }

  const remove = (email) => {
    let newValue = value.filter(val => val !== email)
    setValue(newValue)
    if (newValue.length === 0) {
      setError(requiredMessage)
      setTouched(true)
    }
  }

  function handleDelete (email) {
    remove(email)
  }

  function handleInputUpdate (event) {
    if (event) {
      if (!touched) {
        setTouched(true)
      }
      setInputValue(event.target.value)
    }
  }

  return (
    <div className="App">
      <Autocomplete
        inputValue={inputValue}
        onInputChange={handleInputUpdate}
        multiple
        id="tags-filled"
        options={suggestions}
        freeSolo
        value={value}
        disabled={disabled}
        classes={{
          root: classes.root,
          input: classes.input,
        }}
        onChange={handleAdd}
        renderTags={(value) =>
          value.map((option, index) => (
            <MailChip key={index} text={option} onDelete={() => handleDelete(option)}
                      onEdit={() => handleEditClick(option)}/>
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            variant="filled"
            label={placeholder}
            placeholder={placeholder + (isRequired ? ' *' : '')}
            helperText={<ErrorMessage name={name}/>}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true
            }}
          />
        )}
      />
    </div>
  )
}